// Composable
import { createRouter, createWebHistory } from "vue-router";
import type { RouteLocation } from "vue-router";
import authGuard from "@/app/router/authGuard";
import migrationGuard from "@/app/router/migrationGuard";
import kioskGuard from "@/kiosk/kioskGuard";
import roleGuard from "@/app/router/roleGuard";
import pageTitleGuard from "@/app/router/pageTitleGuard";
import i18n from "@/app/plugins/i18n";
import { UserRoleStatus, AccountPlan, NectarStatus } from "@/shared/enums/status.enum";
import type { ProductlaneWidget } from "@/shared/types/global";
import accountStatusGuard from "@/app/router/accountStatusGuard";
import merchantSessionGuard from "@/app/router/merchantSessionGuard";
import planGuard from "@/app/router/planGuard";
import twoFactorGuard from "@/app/router/twoFactorGuard";
import { Pages } from "@/shared/enums/route.enum";
import AccountPage from "@/account/pages/AccountPage.vue";
import CustomersPage from "@/customers/pages/CustomersPage.vue";
import DashboardPage from "@/dashboard/pages/DashboardPage.vue";
import LocationListPage from "@/terminals/pages/LocationListPage.vue";
import PaymentLinksPage from "@/payment-links/pages/PaymentLinksPage.vue";
import PaymentListPage from "@/payments/pages/PaymentListPage.vue";
import ProductListPage from "@/catalog/pages/ProductListPage.vue";
import ReportsPage from "@/reports/pages/ReportsPage.vue";
import TerminalFormsListPage from "@/terminal-forms/pages/TerminalFormsListPage.vue";
import UserListPage from "@/users/pages/UserListPage.vue";

const routes = [
  {
    path: "/:catchAll(.*)",
    redirect: "login",
  },
  {
    path: "/login",
    name: "login",
    meta: {
      title: i18n.global.t("navBar.login"),
      requiresAuth: false,
      redirectAuth: "dashboard",
      hideAppShell: true,
    },
    component: () => import(/* webpackChunkName: "login" */ "@/auth/pages/LoginPage.vue"),
  },
  {
    path: "/register",
    name: "register",
    meta: {
      title: i18n.global.t("navBar.register"),
      requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "register" */ "@/auth/pages/RegisterPage.vue"),
  },
  {
    path: "/terms-of-service",
    name: "termsOfService",
    meta: {
      title: i18n.global.t("navBar.termsOfService"),
      requiresAuth: false,
      hideAppShell: true,
      skipMerchantGuards: true,
    },
    component: () =>
      import(/* webpackChunkName: "termsOfService" */ "@/legal/pages/TermsOfServicePage.vue"),
  },
  {
    path: "/privacy-policy",
    name: "privacyPolicy",
    meta: {
      title: i18n.global.t("navBar.privacyPolicy"),
      requiresAuth: false,
      hideAppShell: true,
      skipMerchantGuards: true,
    },
    component: () =>
      import(/* webpackChunkName: "privacyPolicy" */ "@/legal/pages/PrivacyPolicyPage.vue"),
  },
  {
    path: "/invite/:token?",
    name: "invite",
    meta: {
      title: i18n.global.t("navBar.invite"),
      requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "invite" */ "@/auth/pages/InvitePage.vue"),
  },
  {
    path: "/dashboard",
    name: "dashboard",
    meta: {
      title: i18n.global.t("navBar.dashboard"),
      color: "secondary",
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: DashboardPage,
  },
  {
    path: "/account-setup",
    name: "accountSetup",
    meta: {
      title: i18n.global.t("navBar.accountSetup"),
      color: "secondary",
      activeAccountsOnly: false,
      requiresAuth: true,
      accessStatus: [NectarStatus.NEW],
    },
    component: () =>
      import(/* webpackChunkName: "accountSetup" */ "@/account/pages/AccountSetupPage.vue"),
  },
  {
    path: "/users",
    name: "users",
    meta: {
      title: i18n.global.t("navBar.users"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: UserListPage,
  },
  {
    path: "/locations",
    name: "locations",
    meta: {
      title: i18n.global.t("navBar.readers"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: LocationListPage,
  },
  {
    path: "/hardware-orders",
    name: "hardwareOrders",
    meta: {
      title: i18n.global.t("page.hardwareOrders.title"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER],
      accessStatus: [NectarStatus.ACTIVE],
    },
    props: { view: "orders" },
    component: () => import("@/terminals/pages/HardwareOrdersPage.vue"),
  },
  {
    path: "/hardware-orders/shop",
    name: "hardwareShop",
    meta: {
      title: i18n.global.t("page.hardwareOrders.shopTitle"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER],
      accessStatus: [NectarStatus.ACTIVE],
    },
    props: { view: "shop" },
    component: () => import("@/terminals/pages/HardwareOrdersPage.vue"),
  },
  {
    path: "/locations/:locationId",
    name: "locationDetail",
    meta: {
      title: i18n.global.t("navBar.readers"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () =>
      import(/* webpackChunkName: "locationDetail" */ "@/terminals/pages/LocationDetailPage.vue"),
  },
  {
    path: "/readers",
    redirect: { name: "locations" },
  },
  {
    path: "/readers/detail/:locationId",
    redirect: (to: RouteLocation) => ({
      name: "locationDetail",
      params: { locationId: to.params.locationId },
    }),
  },
  {
    path: "/account",
    name: "account",
    meta: {
      title: i18n.global.t("navBar.account"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER],
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: AccountPage,
  },
  {
    path: "/profile",
    name: "userProfile",
    meta: {
      title: i18n.global.t("page.profile.title"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () =>
      import(/* webpackChunkName: "userProfile" */ "@/account/pages/UserProfilePage.vue"),
  },
  {
    path: "/security/setup-2fa",
    name: "mfaSetup",
    meta: {
      title: i18n.global.t("security.gate.title"),
      requiresAuth: true,
      hideAppShell: true,
    },
    component: () => import(/* webpackChunkName: "mfaSetup" */ "@/auth/pages/MfaSetupPage.vue"),
  },
  {
    path: "/account/settings/:settingsTab?",
    name: "settings",
    meta: {
      title: i18n.global.t("navBar.settings"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/account/pages/AccountSettingsPage.vue"),
  },
  {
    path: "/account/webhooks/deliveries",
    name: "webhookDeliveries",
    meta: {
      title: i18n.global.t("page.settings.webhooks.deliveriesTitle"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () =>
      import(
        /* webpackChunkName: "webhookDeliveries" */ "@/webhooks/pages/WebhookDeliveriesPage.vue"
      ),
  },
  {
    path: "/account/webhooks/deliveries/:deliveryId",
    name: "webhookDeliveryDetails",
    meta: {
      title: i18n.global.t("page.settings.webhooks.deliveryDetailsTitle"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () =>
      import(
        /* webpackChunkName: "webhookDeliveryDetails" */ "@/webhooks/pages/WebhookDeliveryDetailsPage.vue"
      ),
  },
  {
    path: "/payments",
    name: "payments",
    meta: {
      title: i18n.global.t("navBar.payments"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: PaymentListPage,
  },
  {
    path: "/create/payment",
    name: "createPayment",
    meta: {
      title: i18n.global.t("navBar.payments"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () =>
      import(/* webpackChunkName: "processPayment" */ "@/payments/pages/PaymentProcessPage.vue"),
  },
  {
    path: "/create/product",
    name: "createProduct",
    meta: {
      title: i18n.global.t("navBar.createProduct"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "createProduct" */ "@/catalog/pages/ProductCreateAndUpdatePage.vue"
      ),
  },
  {
    path: `/update/product/:id`,
    name: "productUpdate",
    meta: {
      title: i18n.global.t("navBar.updateProduct"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "productUpdate" */ "@/catalog/pages/ProductCreateAndUpdatePage.vue"
      ),
  },
  {
    path: "/products",
    name: "products",
    meta: {
      title: i18n.global.t("navBar.products"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: ProductListPage,
  },
  {
    path: "/product/:id",
    name: "productDetails",
    meta: {
      title: i18n.global.t("navBar.productDetails"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(/* webpackChunkName: "productDetails" */ "@/catalog/pages/ProductDetailsPage.vue"),
  },
  {
    path: "/product-classes",
    name: "productClasses",
    meta: {
      title: i18n.global.t("navBar.productClasses"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(/* webpackChunkName: "productClasses" */ "@/catalog/pages/ProductClassesListPage.vue"),
  },
  {
    path: "/create/product-class",
    name: "createProductClass",
    meta: {
      title: i18n.global.t("navBar.createProductClass"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "createProductClass" */ "@/catalog/pages/ProductClassCreateAndUpdatePage.vue"
      ),
  },
  {
    path: "/update/product-class/:id",
    name: "updateProductClass",
    meta: {
      title: i18n.global.t("navBar.updateProductClass"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "updateProductClass" */ "@/catalog/pages/ProductClassCreateAndUpdatePage.vue"
      ),
  },
  {
    path: "/product-class/:id",
    name: "productClassDetails",
    meta: {
      title: i18n.global.t("navBar.productClassDetails"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "productClassDetails" */ "@/catalog/pages/ProductClassDetailsPage.vue"
      ),
  },
  {
    path: "/payment/:id(pi_[A-Za-z0-9_]+)",
    name: "processPayment",
    meta: {
      title: i18n.global.t("navBar.payments"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () =>
      import(/* webpackChunkName: "processPayment" */ "@/payments/pages/PaymentProcessPage.vue"),
  },
  {
    path: "/payment/:token_id",
    name: "publicPayment",
    meta: {
      title: i18n.global.t("navBar.payments"),
      requiresAuth: false,
      hideAppShell: true,
      skipMerchantGuards: true,
    },
    component: () =>
      import(/* webpackChunkName: "publicPayment" */ "@/payments/pages/PaymentPublicPage.vue"),
  },
  {
    path: "/kiosk",
    name: "kioskLauncher",
    meta: {
      title: i18n.global.t("page.kiosk.launcher.title"),
      requiresAuth: false,
      hideAppShell: true,
      skipMerchantGuards: true,
    },
    component: () =>
      import(/* webpackChunkName: "kioskLauncher" */ "@/kiosk/pages/KioskLauncherPage.vue"),
  },
  {
    path: "/kiosk/:tokenHash",
    component: () => import(/* webpackChunkName: "kioskLayout" */ "@/kiosk/layouts/View.vue"),
    meta: {
      hideAppShell: true,
      skipMerchantGuards: true,
    },
    children: [
      {
        path: "",
        name: "kioskPinLogin",
        meta: {
          title: i18n.global.t("page.kiosk.login.title"),
          requiresAuth: false,
          redirectKioskAuth: "kioskSplash",
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(/* webpackChunkName: "kioskPinLogin" */ "@/kiosk/pages/KioskPinLoginPage.vue"),
      },
      {
        path: "splash",
        name: "kioskSplash",
        meta: {
          title: i18n.global.t("page.kiosk.splash.title"),
          requiresAuth: false,
          requiresKioskAuth: true,
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(/* webpackChunkName: "kioskSplash" */ "@/kiosk/pages/KioskSplashPage.vue"),
      },
      {
        path: "catalog",
        name: "kioskCatalog",
        meta: {
          title: i18n.global.t("page.kiosk.catalog.title"),
          requiresAuth: false,
          requiresKioskAuth: true,
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(/* webpackChunkName: "kioskCatalog" */ "@/kiosk/pages/KioskCatalogPage.vue"),
      },
      {
        path: "catalog/tags/:tagId",
        name: "kioskProductClassTagCatalog",
        meta: {
          title: i18n.global.t("page.kiosk.catalog.title"),
          requiresAuth: false,
          requiresKioskAuth: true,
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(/* webpackChunkName: "kioskCatalog" */ "@/kiosk/pages/KioskCatalogPage.vue"),
      },
      {
        path: "product-classes/:id",
        name: "kioskProductClassDetails",
        meta: {
          title: i18n.global.t("page.kiosk.detail.title"),
          requiresAuth: false,
          requiresKioskAuth: true,
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "kioskProductClassDetails" */ "@/kiosk/pages/KioskProductClassDetailPage.vue"
          ),
      },
      {
        path: "products/:productId",
        name: "kioskProductDetails",
        meta: {
          title: i18n.global.t("page.kiosk.detail.title"),
          requiresAuth: false,
          requiresKioskAuth: true,
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "kioskProductClassDetails" */ "@/kiosk/pages/KioskProductClassDetailPage.vue"
          ),
      },
      {
        path: "context",
        name: "kioskContext",
        meta: {
          title: i18n.global.t("page.kiosk.context.title"),
          requiresAuth: false,
          requiresKioskAuth: true,
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(/* webpackChunkName: "kioskContext" */ "@/kiosk/pages/KioskContextPage.vue"),
      },
      {
        path: "checkout",
        name: "kioskCheckout",
        meta: {
          title: i18n.global.t("page.kiosk.checkout.title"),
          requiresAuth: false,
          requiresKioskAuth: true,
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(/* webpackChunkName: "kioskCheckout" */ "@/kiosk/pages/KioskCheckoutPage.vue"),
      },
      {
        path: "checkout/success",
        name: "kioskCheckoutSuccess",
        meta: {
          title: i18n.global.t("page.kiosk.checkout.successTitle"),
          requiresAuth: false,
          requiresKioskAuth: true,
          hideAppShell: true,
          skipMerchantGuards: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "kioskCheckoutSuccess" */ "@/kiosk/pages/KioskCheckoutSuccessPage.vue"
          ),
      },
    ],
  },
  {
    path: "/payments/details/:paymentIntentId",
    name: "paymentDetails",
    meta: {
      title: i18n.global.t("navBar.payments"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () =>
      import(/* webpackChunkName: "paymentDetails" */ "@/payments/pages/PaymentDetailsPage.vue"),
  },
  {
    path: "/customers",
    name: "customers",
    meta: {
      title: i18n.global.t("navBar.customers"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: CustomersPage,
  },
  {
    path: "/customer/:id",
    name: "customerDetails",
    meta: {
      title: i18n.global.t("navBar.customerDetails"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(/* webpackChunkName: "customerDetails" */ "@/customers/pages/CustomerDetailsPage.vue"),
  },
  {
    path: "/paymentLinks",
    name: "paymentLinks",
    meta: {
      title: i18n.global.t("navBar.paymentLinks"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: PaymentLinksPage,
  },
  {
    path: `/create/paymentLinks`,
    name: "createPaymentLink",
    meta: {
      title: i18n.global.t("navBar.createPaymentLink"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "paymentLinksCreate" */ "@/payment-links/pages/PaymentLinksCreateAndUpdatePage.vue"
      ),
  },
  {
    path: `/paymentLinks/:id`,
    name: "updatePaymentLink",
    meta: {
      title: i18n.global.t("navBar.updatePaymentLink"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "updatePaymentLink" */ "@/payment-links/pages/PaymentLinksCreateAndUpdatePage.vue"
      ),
  },
  {
    path: `/details/paymentLinks/:id`,
    name: "paymentLinksDetail",
    meta: {
      title: i18n.global.t("navBar.paymentLinksDetail"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "paymentLinksDetail" */ "@/payment-links/pages/PaymentLinksDetailPage.vue"
      ),
  },
  {
    path: `/terminalForms`,
    name: "terminalForms",
    meta: {
      title: i18n.global.t("navBar.terminalForms"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: TerminalFormsListPage,
  },
  {
    path: `/terminalForms/create`,
    name: "terminalFormsCreate",
    meta: {
      title: i18n.global.t("navBar.terminalForms"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "terminalFormsCreate" */ "@/terminal-forms/pages/TerminalFormCreateAndUpdatePage.vue"
      ),
  },
  {
    path: `/terminalForms/:id`,
    name: "terminalFormsUpdate",
    meta: {
      title: i18n.global.t("navBar.terminalForms"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "terminalFormsUpdate" */ "@/terminal-forms/pages/TerminalFormCreateAndUpdatePage.vue"
      ),
  },
  {
    path: `/terminalForms/:id/details`,
    name: "terminalFormsDetail",
    meta: {
      title: i18n.global.t("navBar.terminalFormDetail"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "terminalFormsDetail" */ "@/terminal-forms/pages/TerminalFormDetailPage.vue"
      ),
  },
  {
    path: "/inventory-log",
    name: "inventory-log",
    meta: {
      title: i18n.global.t("navBar.inventoryLog"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.ADMIN, UserRoleStatus.OWNER],
      accountStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(/* webpackChunkName: "inventoryLog" */ "@/catalog/pages/InventoryLogPage.vue"),
  },
  {
    path: "/import-data",
    name: "importData",
    meta: {
      title: i18n.global.t("navBar.importData"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () => import("@/import-data/pages/ImportDataPage.vue"),
  },
  {
    path: "/import-data/:importId",
    name: "importDataDetails",
    meta: {
      title: i18n.global.t("navBar.importDataDetails"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () => import("@/import-data/pages/ImportDataDetailsPage.vue"),
  },
  {
    path: "/reports",
    name: "reports",
    meta: {
      title: i18n.global.t("navBar.reports"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: ReportsPage,
  },
  {
    path: "/reports/generated",
    name: "generatedReports",
    meta: {
      title: i18n.global.t("reports.generated.title"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () => import("@/reports/pages/GeneratedReportsPage.vue"),
  },
  {
    path: "/reports/schedules",
    name: "reportSchedules",
    meta: {
      title: i18n.global.t("page.settings.webhooks.schedules.title"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
    },
    component: () => import("@/reports/pages/ReportSchedulesPage.vue"),
  },
  {
    path: "/product-tags",
    name: "productTags",
    meta: {
      title: i18n.global.t("navBar.productTags"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(/* webpackChunkName: "productTags" */ "@/catalog/pages/ProductTagsListPage.vue"),
  },
  {
    path: "/create/product-tag",
    name: "createProductTag",
    meta: {
      title: i18n.global.t("navBar.createProductTag"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(/* webpackChunkName: "createProductTag" */ "@/catalog/pages/ProductTagCreatePage.vue"),
  },
  {
    path: "/product-tag/:id",
    name: "productTagDetails",
    meta: {
      title: i18n.global.t("navBar.productTagDetails"),
      requiresAuth: true,
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(
        /* webpackChunkName: "productTagDetails" */ "@/catalog/pages/ProductTagDetailsPage.vue"
      ),
  },
  {
    path: "/product-tag/edit/:id",
    name: "updateProductTag",
    meta: {
      title: i18n.global.t("navBar.updateProductTag"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () =>
      import(/* webpackChunkName: "updateProductTag" */ "@/catalog/pages/ProductTagUpdatePage.vue"),
  },
  {
    path: "/cart/scan",
    name: Pages.SCANNER,
    meta: {
      title: i18n.global.t("navBar.scanner"),
      requiresAuth: true,
      accessRoles: [UserRoleStatus.OWNER, UserRoleStatus.ADMIN],
      accessStatus: [NectarStatus.ACTIVE],
      plan: [AccountPlan.LITE],
    },
    component: () => import(/* webpackChunkName: "Scanner" */ "@/catalog/pages/ScannerPage.vue"),
  },
];
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(authGuard);
router.beforeEach(migrationGuard);
router.beforeEach(merchantSessionGuard);
router.beforeEach(kioskGuard);
router.beforeEach(roleGuard);
router.beforeEach(accountStatusGuard);
router.beforeEach(twoFactorGuard);
router.beforeEach(planGuard);
router.beforeEach(pageTitleGuard);

const reloadKey = "chunk-reload-attempted";
const routerBase = import.meta.env.BASE_URL.replace(/\/$/, "");
const getReloadPath = (fullPath?: string) => {
  if (!fullPath) {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  return `${routerBase}${fullPath}`;
};

const isProductlaneHiddenRoute = (path: string): boolean =>
  path === "/kiosk" || path.startsWith("/kiosk/");

const syncProductlaneVisibility = (path: string): void => {
  const shouldHide = isProductlaneHiddenRoute(path);
  document.documentElement.classList.toggle("productlane-hidden", shouldHide);

  const productlane = (window as Window & { Productlane?: ProductlaneWidget }).Productlane;
  if (shouldHide) {
    productlane?.close?.();
    productlane?.disable?.();
  } else {
    productlane?.enable?.();
  }
};

router.afterEach((to) => {
  sessionStorage.removeItem(reloadKey);
  syncProductlaneVisibility(to.path);
});

router.onError((error, to) => {
  const message = error.message.toLowerCase();
  const isDynamicImportError =
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed") ||
    message.includes("error loading dynamically imported module");

  if (isDynamicImportError && !sessionStorage.getItem(reloadKey)) {
    sessionStorage.setItem(reloadKey, "1");
    window.location.assign(getReloadPath(to?.fullPath));
  }
});
export default router;
